import type { MarketNewsItem } from '../../services/marketDataApi';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { marketNewsSlug } from '../../utils/liveNews';

type Props = {
  item: MarketNewsItem;
  compact?: boolean;
};

const fallbackImages = [
  '/img/news/gold-01.jpg',
  '/img/news/gold-02.jpg',
  '/img/news/gold-03.jpg',
  '/img/news/gold-04.jpg',
  '/img/news/gold-05.jpg',
  '/img/news/gold-06.jpg',
];

const categoryClass = (type: string) => {
  switch (type.toUpperCase()) {
    case 'FOREX':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'STOCK':
      return 'bg-violet-50 text-violet-700 border-violet-200';
    case 'CRYPTO':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    default:
      return 'bg-yellow-50 text-yellow-700 border-yellow-200';
  }
};

const formatPublishedAt = (value: string, locale: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat(locale, {
    timeZone: 'Asia/Hong_Kong',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

export default function LiveMarketNewsCard({ item, compact = false }: Props) {
  const { t } = useTranslation();
  const fallbackImage = fallbackImages[Math.abs(Number(item.id) || 0) % fallbackImages.length];
  const image = item.imageUrl || fallbackImage;
  const detailPath = `/news/${marketNewsSlug(item.id)}`;
  const symbols = String(item.symbols || '')
    .split(',')
    .map((symbol) => symbol.trim())
    .filter(Boolean)
    .slice(0, 5);

  return (
    <article className="group bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
      <Link to={detailPath} className={`relative block overflow-hidden bg-gray-100 ${compact ? 'h-48' : 'h-56'}`}>
        <img
          src={image}
          alt={item.title}
          loading="lazy"
          decoding="async"
          onError={(event) => {
            const target = event.currentTarget;
            if (!target.src.endsWith(fallbackImage)) target.src = fallbackImage;
          }}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <span
          className={`absolute top-4 left-4 inline-flex px-3 py-1 rounded-full border text-xs font-semibold backdrop-blur-sm ${categoryClass(item.type)}`}
        >
          {t(`news.live.${(item.type || 'GENERAL').toLowerCase()}`, { defaultValue: item.type || 'GENERAL' })}
        </span>
        <span className="absolute bottom-4 left-4 right-4 text-xs font-medium text-white/90">
          {formatPublishedAt(item.publishedAt || item.publishedAtUtc, t('news.dateLocale', { defaultValue: 'en-GB' }))} · {t('markets.hongKongTime', { defaultValue: 'Hong Kong time' })}
        </span>
      </Link>

      <div className={compact ? 'p-5' : 'p-6'}>
        <h3 className={`${compact ? 'text-lg' : 'text-xl'} font-bold text-gray-900 leading-snug line-clamp-2`}>
          <Link to={detailPath} className="transition hover:text-yellow-600">
            {item.title}
          </Link>
        </h3>

        {item.snippet && (
          <p className={`mt-3 text-gray-600 leading-7 ${compact ? 'text-sm line-clamp-3' : 'text-sm line-clamp-4'}`}>
            {item.snippet}
          </p>
        )}

        {symbols.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {symbols.map((symbol) => (
              <span key={symbol} className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-600">
                {symbol}
              </span>
            ))}
          </div>
        )}

        <Link
          to={detailPath}
          className="mt-5 inline-flex items-center gap-2 rounded-xl bg-gray-950 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-yellow-500 hover:text-gray-950"
        >
          {t('news.readMore')}
          <i className="ri-arrow-right-line" aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
}
